/**
 * Fantasy Console 0 - Sprite Actions Component
 *
 * Sprite manipulation actions: clear, copy, paste, flip, rotate, shift,
 * and import/export.
 */

import { useCallback, useRef } from 'react'
import {
  extractSprite,
  insertSprite,
  clearSprite,
  flipSpriteH,
  flipSpriteV,
  rotateSpriteC90,
  rotateSpriteCC90,
  shiftSprite,
  exportSpriteSheetPNG,
  importSpriteSheetPNG,
} from '../../utils/spriteUtils'
import { ActionGroup, IconButton } from '../shared/ActionGroup'
import {
  ClearIcon,
  CopyIcon,
  PasteIcon,
  ImportIcon,
  ExportIcon,
} from '../shared/icons'
import styles from './SpriteActions.module.css'
import editorStyles from '../../styles/editor.module.css'

interface SpriteActionsProps {
  sprites: Uint8Array
  selectedSprite: number
  onSpritesChange: (sprites: Uint8Array) => void
  clipboard: Uint8Array | null
  onClipboardChange: (data: Uint8Array | null) => void
  displaySheetActions?: boolean
  displayEditorActions?: boolean
}

export function SpriteActions({
  sprites,
  selectedSprite,
  onSpritesChange,
  clipboard,
  onClipboardChange,
  displayEditorActions = false,
  displaySheetActions = false,
}: SpriteActionsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Clear current sprite
  const handleClear = useCallback(() => {
    const newSprites = clearSprite(sprites, selectedSprite)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, onSpritesChange])

  // Copy current sprite to clipboard
  const handleCopy = useCallback(() => {
    const spriteData = extractSprite(sprites, selectedSprite)
    onClipboardChange(spriteData)
  }, [sprites, selectedSprite, onClipboardChange])

  // Paste clipboard to current sprite
  const handlePaste = useCallback(() => {
    if (!clipboard) return
    const newSprites = insertSprite(sprites, selectedSprite, clipboard)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, clipboard, onSpritesChange])

  // Flip horizontally
  const handleFlipH = useCallback(() => {
    const spriteData = extractSprite(sprites, selectedSprite)
    const flipped = flipSpriteH(spriteData)
    const newSprites = insertSprite(sprites, selectedSprite, flipped)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, onSpritesChange])

  // Flip vertically
  const handleFlipV = useCallback(() => {
    const spriteData = extractSprite(sprites, selectedSprite)
    const flipped = flipSpriteV(spriteData)
    const newSprites = insertSprite(sprites, selectedSprite, flipped)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, onSpritesChange])

  // Rotate clockwise
  const handleRotateCW = useCallback(() => {
    const spriteData = extractSprite(sprites, selectedSprite)
    const rotated = rotateSpriteC90(spriteData)
    const newSprites = insertSprite(sprites, selectedSprite, rotated)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, onSpritesChange])

  // Rotate counter-clockwise
  const handleRotateCCW = useCallback(() => {
    const spriteData = extractSprite(sprites, selectedSprite)
    const rotated = rotateSpriteCC90(spriteData)
    const newSprites = insertSprite(sprites, selectedSprite, rotated)
    onSpritesChange(newSprites)
  }, [sprites, selectedSprite, onSpritesChange])

  // Shift in direction
  const handleShift = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      const spriteData = extractSprite(sprites, selectedSprite)
      const shifted = shiftSprite(spriteData, direction)
      const newSprites = insertSprite(sprites, selectedSprite, shifted)
      onSpritesChange(newSprites)
    },
    [sprites, selectedSprite, onSpritesChange],
  )

  // Export sprite sheet as PNG
  const handleExport = useCallback(() => {
    exportSpriteSheetPNG(sprites)
  }, [sprites])

  // Import sprite sheet from PNG
  const handleImport = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      try {
        const newSprites = await importSpriteSheetPNG(file)
        onSpritesChange(newSprites)
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to import image')
      }

      // Reset input so the same file can be selected again
      e.target.value = ''
    },
    [onSpritesChange],
  )

  return (
    <div className={editorStyles.actionGroupContainer}>
      {/* Clipboard Actions */}
      {displaySheetActions && (
        <ActionGroup label='Clipboard'>
          <IconButton onClick={handleClear} title='Clear sprite'>
            <ClearIcon />
          </IconButton>
          <IconButton onClick={handleCopy} title='Copy (Ctrl+C)'>
            <CopyIcon />
          </IconButton>
          <IconButton
            onClick={handlePaste}
            disabled={!clipboard}
            title='Paste (Ctrl+V)'
          >
            <PasteIcon />
          </IconButton>
        </ActionGroup>
      )}

      {/* Transform Actions */}
      {displayEditorActions && (
        <ActionGroup label='Transform'>
          <IconButton onClick={handleFlipH} title='Flip Horizontal'>
            <FlipHIcon />
          </IconButton>
          <IconButton onClick={handleFlipV} title='Flip Vertical'>
            <FlipVIcon />
          </IconButton>
          <IconButton
            onClick={handleRotateCCW}
            title='Rotate Counter-clockwise'
          >
            <RotateCCWIcon />
          </IconButton>
          <IconButton onClick={handleRotateCW} title='Rotate Clockwise'>
            <RotateCWIcon />
          </IconButton>
        </ActionGroup>
      )}

      {/* Shift Actions */}
      {displayEditorActions && (
        <ActionGroup label='Shift'>
          <IconButton onClick={() => handleShift('up')} title='Shift Up'>
            <ArrowUpIcon />
          </IconButton>
          <IconButton onClick={() => handleShift('down')} title='Shift Down'>
            <ArrowDownIcon />
          </IconButton>
          <IconButton onClick={() => handleShift('left')} title='Shift Left'>
            <ArrowLeftIcon />
          </IconButton>
          <IconButton onClick={() => handleShift('right')} title='Shift Right'>
            <ArrowRightIcon />
          </IconButton>
        </ActionGroup>
      )}

      {/* Import/Export */}
      {displaySheetActions && (
        <ActionGroup label='File'>
          <IconButton onClick={handleImport} title='Import PNG'>
            <ImportIcon />
          </IconButton>
          <IconButton onClick={handleExport} title='Export PNG'>
            <ExportIcon />
          </IconButton>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/png'
            onChange={handleFileChange}
            className={styles.hiddenInput}
          />
        </ActionGroup>
      )}
    </div>
  )
}

// Icons unique to sprite editor
function FlipHIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M7 2v10' />
      <path d='M3 4h2v6H3zM9 4h2v6H9z' />
      <path d='M1 7h2M11 7h2' />
    </svg>
  )
}

function FlipVIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M2 7h10' />
      <path d='M4 3v2h6V3zM4 9v2h6V9z' />
      <path d='M7 1v2M7 11v2' />
    </svg>
  )
}

function RotateCWIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M11 4a5 5 0 1 0 1 3' />
      <path d='M11 1v3h-3' />
    </svg>
  )
}

function RotateCCWIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M3 4a5 5 0 1 1-1 3' />
      <path d='M3 1v3h3' />
    </svg>
  )
}

function ArrowUpIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M7 12V2M3 6l4-4 4 4' />
    </svg>
  )
}

function ArrowDownIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M7 2v10M3 8l4 4 4-4' />
    </svg>
  )
}

function ArrowLeftIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M12 7H2M6 3L2 7l4 4' />
    </svg>
  )
}

function ArrowRightIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M2 7h10M8 3l4 4-4 4' />
    </svg>
  )
}
